/** Concept V3 Classification Plan config options. */

/** This API version only accepts `insight_only` — UI exposes Insight Only. */
export const TAGGING_APPROACH_OPTIONS = ["Insight Only"] as const;

export type TaggingApproachOption =
  (typeof TAGGING_APPROACH_OPTIONS)[number];

export const EXECUTION_OPTIONS = ["Automated", "On-demand"] as const;

export type ExecutionOption = (typeof EXECUTION_OPTIONS)[number];

export const ASSESSMENT_TEMPLATE_OPTIONS = [
  "Implementation",
  "Tagging",
] as const;

export type AssessmentTemplateOption =
  (typeof ASSESSMENT_TEMPLATE_OPTIONS)[number];

/** OTO plans currently support Implementation only. */
export const OTO_ASSESSMENT_TEMPLATE_OPTIONS = ["Implementation"] as const;

export const OTO_DEFAULT_ASSESSMENT_TEMPLATE =
  OTO_ASSESSMENT_TEMPLATE_OPTIONS[0];

/** Mock Update Assessment Record values (Assessment.GSKfav dimension). */
export const UPDATE_ASSESSMENT_RECORD_DIMENSION = "Assessment.GSKfav";

export const UPDATE_ASSESSMENT_RECORD_VALUES = [
  "Safety Issue",
  "Safety Awareness",
  "Safety Implementation",
  "Safety Consultation",
] as const;

export type UpdateAssessmentRecordValue =
  (typeof UPDATE_ASSESSMENT_RECORD_VALUES)[number];

/** Prompt max length for Classification Plan forms (Concept V3). */
export const CLASSIFICATION_PROMPT_MAX_LENGTH = 5000;

/** Prompt is optional; content is seeded from the default-prompt API. */
export const TAGGING_PROMPT_OPTIONAL_INFO =
  "Describe how the AI should select values from the configured dimension.";

/** @deprecated Prompt is optional; use `TAGGING_PROMPT_OPTIONAL_INFO`. */
export const TAGGING_PROMPT_REQUIRED_INFO = TAGGING_PROMPT_OPTIONAL_INFO;

export function isInsightsObjectType(
  objectType: string,
  objectTypeName?: string,
): boolean {
  const haystack = `${objectType} ${objectTypeName ?? ""}`.toLowerCase();
  return haystack.includes("insight");
}

export function shouldShowTaggingApproach(
  objectType: string,
  objectTypeName?: string,
): boolean {
  return isInsightsObjectType(objectType, objectTypeName);
}

export function shouldShowUpdateRecord(
  assessmentTemplate: string,
): boolean {
  return assessmentTemplate === "Implementation";
}

/** Prompt is never required — default text comes from the API when shown. */
export function isPromptRequiredForTemplate(
  _assessmentTemplate?: string,
): boolean {
  return false;
}

/**
 * Classification Prompt is shown only after Reference Items (object) and
 * Impacted Items (dimension + values / All) are selected. Value is loaded
 * from GET default-prompt.
 */
export function shouldShowPrompt(input: {
  objectType: string;
  conceptCode: string;
  allValues?: boolean;
  conceptValues?: readonly unknown[];
}): boolean {
  if (!input.objectType.trim() || !input.conceptCode.trim()) return false;
  if (input.allValues) return true;
  return (input.conceptValues?.length ?? 0) > 0;
}

export type ClassificationPlanConfigState = {
  taggingApproach: string;
  execution: string;
  /**
   * Execution layer — API `layer` (int ≥ 0). Stored as digit string in the form
   * ("" = unset).
   */
  layer: string;
  assessmentTemplate: string;
  /** Selected Update Assessment Record dimension (`kind=assessment_record`). */
  updateAssessmentRecordConcept: string;
  updateAssessmentRecords: string[];
};

/**
 * Fresh Classification config defaults.
 * Always use this factory when seeding drafts — array fields must not be shared
 * across Add One Item rows.
 */
export function createEmptyClassificationPlanConfig(): ClassificationPlanConfigState {
  return {
    taggingApproach: "",
    execution: "",
    layer: "",
    assessmentTemplate: "",
    updateAssessmentRecordConcept: "",
    updateAssessmentRecords: [],
  };
}

/** Static empty snapshot for tests / initial comparisons (do not mutate). */
export const EMPTY_CLASSIFICATION_PLAN_CONFIG: ClassificationPlanConfigState =
  Object.freeze({
    taggingApproach: "",
    execution: "",
    layer: "",
    assessmentTemplate: "",
    updateAssessmentRecordConcept: "",
    updateAssessmentRecords: Object.freeze([]) as unknown as string[],
  });

/**
 * Highest Layer offered in the Classification Plan dropdown (Layer 0 … 10).
 * Edit forms also include the plan's current layer when it exceeds this max.
 */
export const CLASSIFICATION_LAYER_MAX_OPTION = 10;

/** Tooltip — AC3: auto-classification runs layers in ascending order. */
export const CLASSIFICATION_LAYER_INFO =
  "Active plans run in ascending Layer order (Layer 0 first). Every plan in a layer finishes before the next layer starts, so a higher layer can use tags produced by layers below it.";

/** Parse form Layer text → API int in [0, 10], or null when empty/invalid. */
export function parseClassificationLayer(
  raw: string | number | null | undefined,
): number | null {
  let value: number;
  if (typeof raw === "number") {
    if (!Number.isFinite(raw)) return null;
    value = Math.floor(raw);
  } else {
    const trimmed = String(raw ?? "").trim();
    if (!trimmed || !/^\d+$/.test(trimmed)) return null;
    value = Number(trimmed);
  }
  if (value < 0 || value > CLASSIFICATION_LAYER_MAX_OPTION) return null;
  return value;
}

/** Display label for a Layer option value (`"0"` → `"Layer 0"`). */
export function formatClassificationLayerLabel(
  layer: string | number,
): string {
  const trimmed = String(layer ?? "").trim();
  if (!trimmed || !/^\d+$/.test(trimmed)) {
    if (typeof layer === "number" && Number.isFinite(layer) && layer >= 0) {
      return `Layer ${Math.floor(layer)}`;
    }
    return "";
  }
  return `Layer ${Number(trimmed)}`;
}

/** Layer dropdown values as digit strings (`"0"` … `"10"`). */
export function getClassificationLayerOptions(): string[] {
  const options: string[] = [];
  for (let i = 0; i <= CLASSIFICATION_LAYER_MAX_OPTION; i += 1) {
    options.push(String(i));
  }
  return options;
}

export function formatUpdateAssessmentRecordDisplay(
  selected: readonly string[],
  dimensionLabel: string = UPDATE_ASSESSMENT_RECORD_DIMENSION,
): string {
  if (selected.length === 0) return "";
  const label = dimensionLabel.trim() || UPDATE_ASSESSMENT_RECORD_DIMENSION;
  return `${label} = ${selected.join(", ")}`;
}
